import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, History, KeyRound, Loader2, Mail, Plus, Trash2, UserMinus, UserPlus, X } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import ExportButton from "./ExportButton";
import { downloadBlob } from "../../lib/downloadBlob";
import { exportName, exportPath } from "../../lib/exportFile";
import { accountState, handoverNote, handoverOptions, inventorySummary, suggestUsername } from "../../lib/trainerAccounts";

/**
 * The Logiscool trainer accounts — the licences, as an inventory (24.09.2026).
 *
 * A licence is paid per year and belongs to the account, so these six seats
 * are lent to formatori one at a time and carry as many cohorts as their
 * holder teaches. The screen answers two questions: who has which seat now,
 * and who had it before.
 *
 * Passwords are read live and shown here only, never in a toast — a toast is
 * the thing people screenshot without thinking.
 */
export default function TrainerAccounts({ trainers, canMutate, onChanged, assignRequest, onAssignHandled }) {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(null);
  const [creating, setCreating] = useState(null);
  const [creds, setCreds] = useState(null);
  const [history, setHistory] = useState(null);
  // Handing a seat over, releasing it, resetting a password, deleting a
  // row: each asks something here rather than in a browser box, so the
  // question can carry a picker and say what it will do.
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/trainer-accounts/inventory");
      setRows(r.data.items || []);
      setSummary(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut încărca conturile."));
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!assignRequest) return;
    setDialog({ kind: "assign", trainer: assignRequest.trainer, accountId: "", resetPassword: false });
    onAssignHandled?.();
  }, [assignRequest, onAssignHandled]);

  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setDialog(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialog]);

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Operațiunea nu a reușit."));
    } finally {
      setBusy(null);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    await run("new", async () => {
      await api.post("/admin/trainer-accounts", creating);
      toast.success("Cont creat. Îl poți crea acum și în Logiscool.");
      setCreating(null);
    });
  };

  const pushToLimo = (a) => run(`limo-${a.id}`, async () => {
    const r = await api.post(`/admin/trainer-accounts/${a.id}/limo/create`);
    if (!r.data.ok) { toast.error(`Logiscool a răspuns: ${r.data.error}. ${r.data.body || ""}`); return; }
    if (r.data.username_taken) {
      toast.error(`Numele de utilizator „${r.data.username_taken}” era deja folosit la Logiscool, `
        + "deci contul s-a creat fără login. Șterge contul și creează-l cu alt utilizator.", { duration: 15000 });
      return;
    }
    toast.success(r.data.already ? "Contul era deja în Logiscool." : "Cont creat în Logiscool.");
  });

  const hold = () => {
    const { kind, resetPassword } = dialog;
    const a = kind === "assign" ? (rows || []).find((r) => r.id === dialog.accountId) : dialog.account;
    const trainer = kind === "assign" ? dialog.trainer : trainers.find((t) => t.id === dialog.trainerId);
    if (!a) return;
    if (!trainer) return;
    setDialog(null);
    run(`hold-${a.id}`, async () => {
      const r = await api.post(`/admin/trainer-accounts/${a.id}/hold`,
        { trainer_id: trainer.id, reset_password: Boolean(resetPassword) });
      if (r.data.password) setCreds({ id: a.id, username: a.username, password: r.data.password });
      toast.success(`Contul „${a.label}” e acum la ${r.data.holder}.`
        + (r.data.groups ? ` Are ${r.data.groups} grupe atribuite.` : ""));
    });
  };

  const release = () => {
    const { account: a, resetPassword } = dialog;
    setDialog(null);
    run(`release-${a.id}`, async () => {
      await api.post(`/admin/trainer-accounts/${a.id}/release`, null,
        { params: { reset_password: Boolean(resetPassword) } });
      toast.success(`Contul „${a.label}” e liber.`);
    });
  };

  const view = (a) => run(`creds-${a.id}`, async () => {
    const r = await api.get(`/admin/trainer-accounts/${a.id}/limo/credentials`);
    if (!r.data.ok) {
      toast.error(r.data.error === "trainer_not_found"
        ? `Logiscool nu are contul în lista unității (a returnat ${r.data.listed ?? 0} conturi).`
        : `Logiscool nu a returnat datele (${r.data.error}).`);
      return;
    }
    setCreds({ id: a.id, username: r.data.username, password: r.data.password });
  });

  const sendAccess = () => {
    const { account: a, toEmail } = dialog;
    setDialog(null);
    run(`send-${a.id}`, async () => {
      const r = await api.post(`/admin/trainer-accounts/${a.id}/send-access`,
        { to_email: toEmail || null });
      if (r.data.sent) { toast.success(`Datele au plecat la ${r.data.to}.`); return; }
      toast.error(r.data.reason === "password_changed_by_user"
        ? "Parola a fost schimbată din Logiscool, deci nu o mai știm. Resetează parola și trimite din nou."
        : "Emailul nu a plecat. Încearcă din nou sau verifică datele contului.");
    });
  };

  const reset = () => {
    const a = dialog.account;
    setDialog(null);
    run(`reset-${a.id}`, async () => {
      const r = await api.post(`/admin/trainer-accounts/${a.id}/limo/reset-password`);
      if (!r.data.ok) { toast.error("Resetarea nu a reușit."); return; }
      setCreds({ id: a.id, username: r.data.username, password: r.data.password });
      toast.success("Parolă nouă generată. O vezi mai jos, o singură dată.");
    });
  };

  const exportHistory = (format) => downloadBlob(exportPath("/admin/trainer-accounts/history.csv", null, format),
    exportName("istoric-conturi-trainer", format));

  const remove = () => {
    const a = dialog.account;
    setDialog(null);
    run(`del-${a.id}`, async () => {
      await api.delete(`/admin/trainer-accounts/${a.id}`);
      toast.success(`Contul „${a.label}” a fost șters din inventar.`);
    });
  };

  const showHistory = (a) => run(`hist-${a.id}`, async () => {
    const r = await api.get(`/admin/trainer-accounts/${a.id}/history`);
    setHistory({ label: a.label, items: r.data.items || [] });
  });

  // Who the open picker can offer — the free-seat rule lives in the lib.
  const options = dialog?.kind === "hold" ? handoverOptions(dialog.account, trainers, rows || []) : [];
  // The seats nobody holds — what a person without one can be given.
  const free = dialog?.kind === "assign" ? (rows || []).filter((a) => !a.holder) : [];

  const btn = "inline-flex items-center gap-1 rounded-full border border-[#E1D7E8] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-50";
  const date = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO") : "");

  return (
    <section id="licente-logiscool" data-testid="trainer-accounts"
      className="rounded-2xl border border-[#E1D7E8] bg-[#FBF8FC] p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="inline-flex items-center gap-1.5 font-semibold text-[#373A45]">
            <KeyRound size={15} className="text-[#965EA7]" /> Licențe Logiscool
          </h3>
          <p className="mt-1 text-xs text-[#6B7280]">
            Cele șase licențe, plătite pe an. O licență ține de cont, nu de grupă: contul stă la un
            singur formator odată și acoperă toate grupele lui.
          </p>
          {summary && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="flex gap-1" aria-hidden="true">
                {(rows || []).map((a) => (
                  <span key={a.id} title={a.holder ? `${a.label} — ${a.holder.name}` : `${a.label} — liber`}
                    className={`h-2.5 w-2.5 rounded-full ${a.holder ? "bg-[#965EA7]" : "border border-[#C9BBD2] bg-white"}`} />
                ))}
              </span>
              <span className="text-xs font-semibold text-[#373A45]" data-testid="trainer-accounts-summary">
                {inventorySummary(summary)}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButton onExport={exportHistory} label="Export istoric"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6]"
            testid="trainer-accounts-export" />
          {canMutate && (
          <button type="button" onClick={() => setCreating({ label: "", email: "", username: "", notes: "" })}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] hover:bg-[#7A4986] px-4 py-1.5 text-sm font-semibold text-white"
            data-testid="trainer-account-new">
            <Plus size={14} /> Cont nou
          </button>
          )}
        </div>
      </div>

      {creating && (
        <form onSubmit={create} className="mt-3 grid gap-2 rounded-xl bg-[#F5F6F6] p-3 sm:grid-cols-4"
          data-testid="trainer-account-form">
          <input required value={creating.label} onChange={(e) => setCreating({ ...creating, label: e.target.value })}
            placeholder="Nume cont (ex. Trainer 1)" className="rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-sm" />
          <input required type="email" value={creating.email}
            /* The username follows the address until somebody types their own:
               it has to be visible and editable, because Logiscool silently
               creates an account with no login when the name is taken. */
            onChange={(e) => setCreating({
              ...creating, email: e.target.value,
              username: creating.username && creating.username !== suggestUsername(creating.email)
                ? creating.username : suggestUsername(e.target.value),
            })}
            placeholder="trainer1@fped.org" className="rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-sm" />
          <input required value={creating.username} onChange={(e) => setCreating({ ...creating, username: e.target.value })}
            placeholder="trainer.fped1" className="rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-sm" />
          <div className="flex gap-2">
            <button type="submit" disabled={busy === "new"}
              className="rounded-full bg-[#965EA7] px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60">
              Salvează
            </button>
            <button type="button" onClick={() => setCreating(null)} className="rounded-full px-3 py-1.5 text-sm">
              Renunță
            </button>
          </div>
        </form>
      )}

      {rows === null ? (
        <p className="mt-3 text-xs text-[#6B7280]">Se încarcă…</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-xs text-[#6B7280]">
          Nicio licență adăugată încă. Creează câte aveți — pentru voi, șase.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr className="text-left">
                <th className="py-1.5 pr-3">Cont</th>
                <th className="py-1.5 pr-3">Cine îl are</th>
                <th className="py-1.5 pr-3">Grupe</th>
                <th className="py-1.5">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const state = accountState(a);
                return (
                  <tr key={a.id} className="border-t border-[#E5E7EB] align-top" data-testid={`trainer-account-${a.id}`}>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-[#373A45]">{a.label}</div>
                      <div className="text-[11px] text-[#6B7280]">{a.username} · {a.email}</div>
                      <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${state.cls}`}>
                        {state.label}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      {a.holder ? (
                        <>
                          <div className="text-[#373A45]">{a.holder.name}</div>
                          <div className="text-[11px] text-[#6B7280]">din {date(a.holder.since)}</div>
                        </>
                      ) : <span className="text-[11px] text-[#6B7280]">liber</span>}
                      {a.access_sent_at && (
                        <div className="text-[11px] text-[#6B7280]">datele trimise pe {date(a.access_sent_at)}</div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-[11px] text-[#6B7280]">
                      {a.groups.length === 0 ? "—" : (
                        <ul className="space-y-0.5">
                          {a.groups.map((g) => <li key={g.id}>{g.name}</li>)}
                        </ul>
                      )}
                    </td>
                    <td className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {canMutate && !a.created_in_limo && (
                          <button type="button" className={btn} disabled={Boolean(busy)}
                            onClick={() => pushToLimo(a)} data-testid={`account-create-limo-${a.id}`}>
                            {busy === `limo-${a.id}` ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                            Creează în Logiscool
                          </button>
                        )}
                        {canMutate && (
                          <button type="button" className={btn} disabled={Boolean(busy)} onClick={() => setDialog({ kind: "hold", account: a, trainerId: "", resetPassword: false })}
                            data-testid={`account-hold-${a.id}`}>
                            <UserPlus size={11} /> {a.holder ? "Predă altui formator" : "Dă unui formator"}
                          </button>
                        )}
                        {canMutate && a.holder && (
                          <button type="button" className={btn} disabled={Boolean(busy)} onClick={() => setDialog({ kind: "release", account: a, resetPassword: false })}
                            data-testid={`account-release-${a.id}`}>
                            <UserMinus size={11} /> Eliberează
                          </button>
                        )}
                        {canMutate && a.created_in_limo && (
                          <>
                            <button type="button" className={btn} disabled={Boolean(busy)} onClick={() => view(a)}>
                              <Eye size={11} /> Vezi datele
                            </button>
                            <button type="button" className={btn} disabled={Boolean(busy)}
                              onClick={() => setDialog({ kind: "send", account: a, toEmail: a.holder?.email || "" })}
                              data-testid={`account-send-${a.id}`}>
                              {busy === `send-${a.id}` ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />}
                              Trimite datele
                            </button>
                            <button type="button" className={btn} disabled={Boolean(busy)} onClick={() => setDialog({ kind: "reset", account: a })}>
                              <KeyRound size={11} /> Resetează parola
                            </button>
                          </>
                        )}
                        <button type="button" className={btn} disabled={Boolean(busy)} onClick={() => showHistory(a)}
                          data-testid={`account-history-${a.id}`}>
                          <History size={11} /> Istoric
                        </button>
                        {canMutate && !a.holder && a.groups.length === 0 && (
                          <button type="button" disabled={Boolean(busy)} onClick={() => setDialog({ kind: "delete", account: a })}
                            className="inline-flex items-center gap-1 rounded-full border border-[#E63946]/30 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#9b1f2c] hover:bg-[#FDECEC] disabled:opacity-50"
                            data-testid={`account-delete-${a.id}`}>
                            <Trash2 size={11} /> Șterge
                          </button>
                        )}
                        {canMutate && (a.holder || a.groups.length > 0) && (
                          <span className="text-[10px] text-[#6B7280]" data-testid={`account-delete-blocked-${a.id}`}>
                            {a.holder
                              ? "pentru ștergere: eliberează-l întâi"
                              : `pentru ștergere: mută întâi ${a.groups.length === 1 ? "grupa" : "cele "
                                + a.groups.length + " grupe"} pe alt cont`}
                          </span>
                        )}
                      </div>
                      {creds?.id === a.id && (
                        <div className="relative mt-1.5 rounded-lg bg-[#F0E7F4] px-2.5 py-1.5 font-mono text-[11px] text-[#373A45]">
                          <button type="button" onClick={() => setCreds(null)} className="absolute right-1 top-1 text-[#6B7280]" aria-label="Ascunde">
                            <X size={11} />
                          </button>
                          <div>utilizator: <span className="select-all">{creds.username || "—"}</span></div>
                          <div>parolă: {creds.password
                            ? <span className="select-all">{creds.password}</span>
                            : <span className="font-sans text-[#8a6a1a]">schimbată din Logiscool; resetează-o</span>}</div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {history && (
        <div className="mt-3 rounded-xl border border-[#E5E7EB] p-3" data-testid="trainer-account-history">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-[#373A45]">Istoric · {history.label}</div>
            <button type="button" onClick={() => setHistory(null)} className="text-[#6B7280]" aria-label="Închide">
              <X size={14} />
            </button>
          </div>
          {history.items.length === 0 ? (
            <p className="mt-2 text-xs text-[#6B7280]">Contul nu a fost dat încă nimănui.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-xs">
              {history.items.map((h) => (
                <li key={h.id} className="flex flex-wrap justify-between gap-2 border-b border-[#E5E7EB] pb-1">
                  <span className="font-medium text-[#373A45]">{h.trainer_name}</span>
                  <span className="text-[#6B7280]">
                    {date(h.from)} – {h.to ? date(h.to) : "acum"}
                    {h.by ? ` · alocat de ${h.by}` : ""}
                    {h.password_reset_at_handover ? " · parolă resetată" : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {dialog?.kind === "hold" && (
        <Dialog title={dialog.account.holder ? "Predă contul altui formator" : "Dă contul unui formator"}
          confirmLabel="Dă contul" onClose={() => setDialog(null)} onConfirm={hold}
          disabled={!dialog.trainerId} testId="account-hold-dialog">
          <p>{handoverNote(dialog.account)}</p>
          {options.length === 0 ? (
            <p className="rounded-xl bg-[#FDF3E7] px-3 py-2 text-xs text-[#8a6a1a]">
              Nu e niciun formator căruia să-i dai contul. Adaugă unul cu „Formator nou”,
              sau eliberează contul pe care îl ține deja.
            </p>
          ) : (
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">Formator</span>
              <select value={dialog.trainerId} autoFocus
                onChange={(e) => setDialog({ ...dialog, trainerId: e.target.value })}
                className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2 text-sm"
                data-testid="account-hold-trainer">
                <option value="">Alege formatorul…</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id} disabled={o.disabled}>
                    {o.name}{o.note ? ` — ${o.note}` : ""}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[11px] text-[#6B7280]">
                Un formator ține un singur cont odată, deci cei care au deja unul apar, dar nu pot fi aleși.
              </span>
            </label>
          )}
          {dialog.account.created_in_limo && (
            <ResetChoice dialog={dialog} setDialog={setDialog}
              text="Resetez parola și ți-o arăt o singură dată, ca s-o dai formatorului nou." />
          )}
        </Dialog>
      )}

      {dialog?.kind === "assign" && (
        <Dialog title={`Dă o licență lui ${dialog.trainer.name}`} confirmLabel="Dă licența"
          onClose={() => setDialog(null)} onConfirm={hold}
          disabled={!dialog.accountId} testId="account-assign-dialog">
          <p>
            {dialog.trainer.name} primește unul dintre conturile libere și predă cu el la toate
            grupele lui. Licența rămâne consumată până la finalul anului.
          </p>
          {free.length === 0 ? (
            <p className="rounded-xl bg-[#FDF3E7] px-3 py-2 text-xs text-[#8a6a1a]">
              Toate licențele sunt la cineva. Eliberează una de la un formator care nu mai predă,
              sau cumpărați încă una.
            </p>
          ) : (
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">Contul</span>
              <select value={dialog.accountId} autoFocus
                onChange={(e) => setDialog({ ...dialog, accountId: e.target.value })}
                className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2 text-sm"
                data-testid="account-assign-select">
                <option value="">Alege contul…</option>
                {free.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}{a.created_in_limo ? "" : " — încă necreat în Logiscool"}
                  </option>
                ))}
              </select>
            </label>
          )}
          {(rows || []).find((a) => a.id === dialog.accountId)?.created_in_limo && (
            <ResetChoice dialog={dialog} setDialog={setDialog}
              text="Resetez parola și ți-o arăt o singură dată, ca s-o dai formatorului." />
          )}
        </Dialog>
      )}

      {dialog?.kind === "send" && (
        <Dialog title="Trimite datele pe email" confirmLabel="Trimite"
          onClose={() => setDialog(null)} onConfirm={sendAccess}
          disabled={!dialog.toEmail} testId="account-send-dialog">
          <p>
            Trimit utilizatorul și parola contului „{dialog.account.label}”
            {dialog.account.holder ? ` lui ${dialog.account.holder.name}` : ""}, cu mențiunea că e
            un cont de lucru și că parola nu se schimbă.
          </p>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">Adresa</span>
            <input type="email" value={dialog.toEmail} autoFocus
              onChange={(e) => setDialog({ ...dialog, toEmail: e.target.value })}
              placeholder="adresa formatorului"
              className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2 text-sm"
              data-testid="account-send-email" />
            <span className="mt-1 block text-[11px] text-[#6B7280]">
              Propusă din fișa formatorului care ține contul. O poți schimba, de exemplu pe adresa lui personală.
            </span>
          </label>
          <p className="text-xs text-[#6B7280]">
            Parola se citește acum de la Logiscool și nu se salvează nicăieri. Dacă formatorul și-a
            schimbat-o singur, nu o mai știm — atunci resetează parola și trimite din nou.
          </p>
        </Dialog>
      )}

      {dialog?.kind === "release" && (
        <Dialog title="Eliberează contul" confirmLabel="Eliberează"
          onClose={() => setDialog(null)} onConfirm={release} testId="account-release-dialog">
          <p>
            Contul „{dialog.account.label}” nu mai stă la {dialog.account.holder?.name} și devine liber
            pentru alt formator.
          </p>
          <p className="text-xs text-[#6B7280]">
            Licența rămâne consumată până la finalul anului — o eliberare la noi nu o dă înapoi la Logiscool.
          </p>
          {dialog.account.created_in_limo && (
            <ResetChoice dialog={dialog} setDialog={setDialog}
              text="Resetez parola, ca vechiul formator să nu mai poată intra." />
          )}
        </Dialog>
      )}

      {dialog?.kind === "reset" && (
        <Dialog title="Parolă nouă" confirmLabel="Generează parola"
          onClose={() => setDialog(null)} onConfirm={reset} testId="account-reset-dialog">
          <p>Generez o parolă nouă pentru „{dialog.account.label}”. Cea veche nu mai merge.</p>
          <p className="text-xs text-[#6B7280]">
            O vezi o singură dată, aici în tabel. Nu se trimite niciun email; o dai tu formatorului.
          </p>
        </Dialog>
      )}

      {dialog?.kind === "delete" && (
        <Dialog title="Șterge contul din inventar" confirmLabel="Șterge" danger
          onClose={() => setDialog(null)} onConfirm={remove} testId="account-delete-dialog">
          <p>Scot contul „{dialog.account.label}” din inventarul de aici.</p>
          <p className="text-xs text-[#6B7280]">
            La Logiscool contul rămâne — nu îl ștergem noi, pentru că nu e al nostru și licența e plătită
            pe an. Dispare doar de aici, împreună cu posibilitatea de a-l mai da cuiva. Istoricul rămâne.
          </p>
        </Dialog>
      )}
    </section>
  );
}

/** The password question, asked inside the dialog instead of after it. */
function ResetChoice({ dialog, setDialog, text }) {
  return (
    <label className="flex items-start gap-2 rounded-xl bg-[#F5F6F6] px-3 py-2 text-xs text-[#373A45]">
      <input type="checkbox" checked={Boolean(dialog.resetPassword)} className="mt-0.5 h-4 w-4 accent-[#965EA7]"
        onChange={(e) => setDialog({ ...dialog, resetPassword: e.target.checked })}
        data-testid="account-reset-password" />
      <span>{text}</span>
    </label>
  );
}

function Dialog({ title, children, confirmLabel, onConfirm, onClose, disabled, danger, testId }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onClose} role="presentation">
      <div className="w-full max-w-md rounded-3xl border border-[#E5E7EB] bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" data-testid={testId}>
        <div className="flex items-center justify-between border-b border-[#E5E7EB] p-5">
          <h4 className="font-display text-lg font-bold text-[#373A45]">{title}</h4>
          <button type="button" onClick={onClose} aria-label="Închide" className="text-[#6B7280] hover:text-[#373A45]">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 p-5 text-sm text-[#373A45]">{children}</div>
        <div className="flex justify-end gap-2 border-t border-[#E5E7EB] bg-[#F5F6F6]/60 p-5">
          <button type="button" onClick={onClose}
            className="rounded-full border border-[#E5E7EB] px-4 py-2 text-sm hover:bg-white">
            Renunță
          </button>
          <button type="button" onClick={onConfirm} disabled={disabled}
            className={`rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60 ${
              danger ? "bg-[#9b1f2c] hover:bg-[#7d1923]" : "bg-[#965EA7] hover:bg-[#7A4986]"}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
